import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402/api';

const getAxiosInstance = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
};

export interface PatronNote {
  id: string;
  patronId: string;
  authorId: string;
  content: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PatronTag {
  id: string;
  name: string;
  color?: string;
  description?: string;
}

export const patronCrmService = {
  // Notes
  listNotes: async (patronId: string) => {
    const api = getAxiosInstance();
    const { data } = await api.get(`/creator-crm/notes/${encodeURIComponent(patronId)}`);
    return data.notes as PatronNote[];
  },
  createNote: async (payload: { patronId: string; content: string; pinned?: boolean }) => {
    const api = getAxiosInstance();
    const { data } = await api.post('/creator-crm/notes', payload);
    return data.note as PatronNote;
  },
  updateNote: async (noteId: string, updates: Partial<Pick<PatronNote, 'content' | 'pinned'>>) => {
    const api = getAxiosInstance();
    const { data } = await api.put(`/creator-crm/notes/${encodeURIComponent(noteId)}`, updates);
    return data.note as PatronNote;
  },
  deleteNote: async (noteId: string) => {
    const api = getAxiosInstance();
    await api.delete(`/creator-crm/notes/${encodeURIComponent(noteId)}`);
    return true;
  },

  // Tags
  listTags: async () => {
    const api = getAxiosInstance();
    const { data } = await api.get('/creator-crm/tags');
    return data.tags as PatronTag[];
  },
  createTag: async (payload: { name: string; color?: string; description?: string }) => {
    const api = getAxiosInstance();
    const { data } = await api.post('/creator-crm/tags', payload);
    return data.tag as PatronTag;
  },
  updateTag: async (tagId: string, updates: Partial<Omit<PatronTag, 'id'>>) => {
    const api = getAxiosInstance();
    const { data } = await api.put(`/creator-crm/tags/${encodeURIComponent(tagId)}`, updates);
    return data.tag as PatronTag;
  },
  deleteTag: async (tagId: string) => {
    const api = getAxiosInstance();
    await api.delete(`/creator-crm/tags/${encodeURIComponent(tagId)}`);
    return true;
  },
  assignTag: async (tagId: string, patronId: string) => {
    const api = getAxiosInstance();
    const { data } = await api.post(`/creator-crm/tags/${encodeURIComponent(tagId)}/assign`, { patronId });
    return data.assignment as { id: string };
  },
  unassignTag: async (tagId: string, patronId: string) => {
    const api = getAxiosInstance();
    await api.delete(`/creator-crm/tags/${encodeURIComponent(tagId)}/assign/${encodeURIComponent(patronId)}`);
    return true;
  },
  getPatronTags: async (patronId: string) => {
    const api = getAxiosInstance();
    const { data } = await api.get(`/creator-crm/patrons/${encodeURIComponent(patronId)}/tags`);
    return data.tags as PatronTag[];
  }
};
